/**
 * Dialog para iniciar conversa com um contato
 * 
 * Permite selecionar uma inbox WhatsApp ativa e navegar
 * para o chat com o contato selecionado.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, MessageSquare, Phone, Inbox } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getStorageUrl } from '@/lib/storage';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';

// Interface para inbox ativa
interface ActiveInbox {
  id: string;
  name: string;
  identifier: string;
}

// Interface para contato (importada da página de contatos)
interface ContactForChat {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface StartConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactForChat | null;
  tenantId: string;
}

export function StartConversationDialog({
  open,
  onOpenChange,
  contact,
  tenantId,
}: StartConversationDialogProps) {
  const navigate = useNavigate();
  const [inboxes, setInboxes] = useState<ActiveInbox[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingInboxes, setLoadingInboxes] = useState(true);

  // Busca inboxes WhatsApp ativas quando o dialog abre
  useEffect(() => {
    if (open && tenantId) {
      fetchActiveInboxes();
    }
  }, [open, tenantId]);

  // Seleciona automaticamente se houver apenas uma inbox
  useEffect(() => {
    if (inboxes.length === 1) {
      setSelectedInboxId(inboxes[0].id);
    }
  }, [inboxes]);

  const fetchActiveInboxes = async () => {
    setLoadingInboxes(true);
    try {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, identifier')
        .eq('tenant_id', tenantId)
        .in('channel_type', ['whatsapp', 'whatsapp_oficial'])
        .eq('status', 'active');

      if (error) throw error;
      setInboxes(data || []);
    } catch (error) {
      console.error('Erro ao buscar inboxes:', error);
      toast.error('Erro ao carregar inboxes');
    } finally {
      setLoadingInboxes(false);
    }
  };

  // Iniciar conversa e navegar para o chat
  const handleStartConversation = async () => {
    if (!contact || !selectedInboxId) return;

    setLoading(true);
    try {
      // Navega para o chat passando os dados do contato via state
      navigate('/tenant/chat', {
        state: {
          contactId: contact.id,
          contactPhone: contact.phone,
          contactName: contact.name,
          inboxId: selectedInboxId,
        },
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao iniciar conversa:', error);
      toast.error('Erro ao iniciar conversa');
    } finally {
      setLoading(false);
    }
  };

  // Obtém iniciais do nome para o avatar
  const getInitials = (name: string | null) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Iniciar Conversa
          </DialogTitle>
          <DialogDescription>
            Selecione uma inbox para iniciar conversa com este contato.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Informações do contato */}
          <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
            <Avatar className="h-12 w-12">
              <AvatarImage src={getStorageUrl(contact.avatar_url)} />
              <AvatarFallback className={cn("text-lg", getAvatarColorClasses(contact.name))}>
                {getInitials(contact.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">
                {contact.name || 'Sem nome'}
              </p>
              {contact.phone && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {contact.phone}
                </p>
              )}
            </div>
          </div>

          {/* Seleção de inbox */}
          <div className="space-y-2">
            <Label htmlFor="inbox-select">
              <div className="flex items-center gap-2">
                <Inbox className="h-4 w-4" />
                Inbox WhatsApp
              </div>
            </Label>

            {loadingInboxes ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : inboxes.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <p className="text-sm">Nenhuma inbox WhatsApp ativa encontrada.</p>
                <p className="text-xs mt-1">
                  Configure uma inbox na página de Inboxes.
                </p>
              </div>
            ) : (
              <Select
                value={selectedInboxId}
                onValueChange={setSelectedInboxId}
              >
                <SelectTrigger id="inbox-select">
                  <SelectValue placeholder="Selecione uma inbox" />
                </SelectTrigger>
                <SelectContent>
                  {inboxes.map((inbox) => (
                    <SelectItem key={inbox.id} value={inbox.id}>
                      <div className="flex flex-col">
                        <span>{inbox.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {inbox.identifier}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleStartConversation}
            disabled={loading || !selectedInboxId || inboxes.length === 0}
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Iniciar Conversa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
